import type { ISODateTimeString, NumberFormat } from '@gat-ship/types'
import { isISODateTimeString } from '@gat-ship/types'
import { Temporal, Intl } from '@js-temporal/polyfill'
import { ref } from 'vue'

export const currentTimeZone = ref(
  Intl.DateTimeFormat().resolvedOptions().timeZone
)

export function convertTimestampToISODateTimeString(
  timestamp: number
): ISODateTimeString {
  return Temporal.Instant.from(
    new Date(timestamp).toISOString()
  ).toString() as ISODateTimeString
}

export function setCurrentTimeZone(timeZone: string) {
  currentTimeZone.value = timeZone
}

export function getCurrentTimeZone() {
  return currentTimeZone.value
}

export const currentLocale = ref(Intl.DateTimeFormat().resolvedOptions().locale)

export function setCurrentLocale(locale: string) {
  currentLocale.value = locale
}

export function getCurrentLocale() {
  return currentLocale.value
}

export const currentNumberFormat = ref<NumberFormat>('en-US')

export function setCurrentNumberFormat(format: NumberFormat) {
  currentNumberFormat.value = format
}

export function getCurrentNumberFormat() {
  return currentNumberFormat.value
}

export function dateForever() {
  return '2099-12-31T00:00:00Z' as ISODateTimeString
}

export function convertInstantToZonedDateTime(
  date: Temporal.Instant,
  timeZone?: string
) {
  const _timeZone = timeZone ?? currentTimeZone.value
  return date.toZonedDateTimeISO(_timeZone)
}

export function convertISODateTimeStringToZonedDateTime(
  date: ISODateTimeString,
  timeZone?: string
) {
  const _timeZone = timeZone ?? currentTimeZone.value
  return Temporal.Instant.from(date).toZonedDateTimeISO(_timeZone)
}

export function getDurationBetweenISODateTimeStrings(
  date1: ISODateTimeString,
  date2: ISODateTimeString
) {
  const _date1 = convertISODateTimeStringToZonedDateTime(date1)
  const _date2 = convertISODateTimeStringToZonedDateTime(date2)
  return _date1.since(_date2)
}

/**
 * Combines a calendar day and a local wall-clock time into the UTC
 * instant they represent in `timeZone`.
 *
 * The day is read from `date` (a UTC instant — typically a noon-anchored
 * value from a date input) in `dateTimeZone`, then re-anchored with
 * `time` interpreted in `timeZone`. A nullish/empty `timeZone` falls
 * back to the current zone.
 *
 * Used to turn an ETA expressed as "calendar day + named/exact
 * time-of-day at the port" into the `etaAtPort` instant the API expects.
 *
 * @param params.date — UTC instant carrying the chosen calendar day.
 * @param params.time — `HH:mm` or `HH:mm:ss` wall-clock; `00:00` when omitted.
 * @param params.timeZone — IANA zone the wall-clock time is in (the port's).
 * @param params.dateTimeZone — zone to read the calendar day from `date`;
 *   defaults to the current zone (matching how date inputs display it).
 */
export function combineLocalDateTimeToInstant(params: {
  date: ISODateTimeString
  time?: string
  timeZone?: string | null
  dateTimeZone?: string
}): ISODateTimeString {
  const dateZone = params.dateTimeZone ?? currentTimeZone.value
  const targetZone = params.timeZone || currentTimeZone.value
  const plainDate = Temporal.Instant.from(params.date)
    .toZonedDateTimeISO(dateZone)
    .toPlainDate()
  const plainTime = Temporal.PlainTime.from(params.time ?? '00:00')
  return plainDate
    .toPlainDateTime(plainTime)
    .toZonedDateTime(targetZone)
    .toInstant()
    .toString() as ISODateTimeString
}

export function dateNow(timeZone?: string) {
  const _timeZone = timeZone ?? currentTimeZone.value

  return Temporal.Now.zonedDateTimeISO(_timeZone)
    .toInstant()
    .toString() as ISODateTimeString
}

export function timeRemaining(
  isoDate: ISODateTimeString,
  largestUnit:
    | 'years'
    | 'months'
    | 'days'
    | 'hours'
    | 'minutes'
    | 'seconds' = 'days'
) {
  const timeZone = Temporal.Now.timeZoneId() // Get current system time zone
  const now = Temporal.Now.zonedDateTimeISO(timeZone)
  const past = Temporal.Instant.from(isoDate).toZonedDateTimeISO(timeZone)
  return past.since(now, { largestUnit })
}

export function timeSince(
  isoDate: ISODateTimeString,
  options?: {
    /** If set to false, the seconds will be shown as 'just now' */
    showSeconds?: boolean
  }
) {
  const timeZone = Temporal.Now.timeZoneId() // Get current system time zone
  const now = Temporal.Now.zonedDateTimeISO(timeZone)
  const past = Temporal.Instant.from(isoDate).toZonedDateTimeISO(timeZone)

  const ago = (value: number, unit: string) =>
    `${value} ${unit}${value > 1 ? 's' : ''} ago`

  const duration = now.since(past, { largestUnit: 'years' })
  if (duration.years > 0) return ago(duration.years, 'year')
  if (duration.months > 0) return ago(duration.months, 'month')
  if (duration.days > 0) return ago(duration.days, 'day')

  const hours = now.since(past, { largestUnit: 'hours' }).hours
  if (hours > 0) return ago(hours, 'hour')

  const minutes = now.since(past, { largestUnit: 'minutes' }).minutes
  if (minutes > 0) return ago(minutes, 'minute')

  if (options?.showSeconds ?? true) {
    return ago(now.since(past, { largestUnit: 'seconds' }).seconds, 'second')
  }
  return 'Just now'
}

export { isISODateTimeString }
